import { memo, useCallback, useEffect, useState } from "react";

import { createContactRelationAPI, getContactRelationsAPI } from "services/api/contacts/api";
import { ContactRelationEditFormValues, ContactRelationRow } from "services/api/contacts/types";
import { initialContactRepresentativeEditForm } from "services/api/contacts/consts";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";

import { ContactRepresentativeEditProps } from "./types";
import { CONTACT_REPRESENTATIVE_EDIT_FORM_SCHEMA } from "./validation";

import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { useTranslate } from "helpers";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { ContactDropdownTypeEnum } from "ui/components/Dropdown/ContactDropdown/types";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";


const ContactRepresentativeEditCard: React.FC<ContactRepresentativeEditProps> = (props) => {
  const { contactId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<ContactRelationEditFormValues>(initialContactRepresentativeEditForm);
  const [existingRelations, setExistingRelations] = useState<ContactRelationRow[]>([]);

  useEffect(() => {
    if (!show) return;
    setFormValues(initialContactRepresentativeEditForm);
    setErrors({});
    fetchExistingRelations();
  }, [show]);

  const fetchExistingRelations = async () => {
    if (!contactId) return;
    try {
      const result = await getContactRelationsAPI(contactId);
      setExistingRelations(result);
    } catch {
      setExistingRelations([]);
    }
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CONTACT_REPRESENTATIVE_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    // Check if person already exists as representative
    const alreadyExists = existingRelations.some(
      (r) => r.personContact?.id === formValues.personContactId
    );

    if (alreadyExists) {
      setErrors((prev) => ({
        ...prev,
        personContactId: "Ova osoba je već dodana kao predstavnik.",
      }));
      return;
    }

    setIsLoading(true);

    try {
      await createContactRelationAPI(contactId, {
        personContactId: formValues.personContactId,
      });
      if (onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

 const handlePersonDropdownChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      setFormValues((old) => ({ ...old, personContactId: item.value }));
      const alreadyExists = existingRelations.some(
        (r) => r.personContact?.id === item.value
      );
      if (alreadyExists) {
        setErrors({ personContactId: "Ova osoba je već dodana kao predstavnik." });
      } else {
        setErrors({});
      }
    },
    [existingRelations]
  );

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      position="centered" size="small">
      <ModalHeader icon="/media/images/add-icon.svg" title={t("admin.contact.representative.add.title")} onHide={onClose} />
      <ModalBody>
        <div className="flex  gap-4 min-h-[300px]">
          <div className="w-full ">
            <div className="flex  gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1">
                    <ContactDropdown
                      name="personContactId"
                      label="Osoba"
                      placeholder="Odaberite osobu"
                      value={formValues.personContactId || null}
                      onChange={handlePersonDropdownChange}
                      contactType={ContactDropdownTypeEnum.PERSON}
                      error={errors.personContactId}
                    />
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={handleCloseModal}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(ContactRepresentativeEditCard);
